// Modern (D-style) rewrite of the Analytics tab — 6 sub-tabs.
//
// Renders when ThemeModernContext = true. Falls back to the legacy
// ReportsTab when toggle is off.

import { useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import {
  PeriodPicker,
  periodToIsoRange,
  DATA_START_MONTH,
  monthTitle,
  currentMonthKey,
  type PeriodValue,
} from "../components/PeriodPicker.js";
import { SelectSheet } from "../components/SelectSheet.js";
import { DateSheet } from "../components/DateSheet.js";
import { MasterDetailModern } from "./Masters.modern.js";
import { ClientDetailModern } from "./Clients.modern.js";
import { useScrollMemory } from "../scrollMemory.js";

type SubTab =
  | "money"
  | "expenses"
  | "trends"
  | "reviews"
  | "resources"
  | "shopping";

const SUB_TABS: { id: SubTab; label: string; emoji: string }[] = [
  { id: "money", label: "Гроші", emoji: "💰" },
  { id: "expenses", label: "Витрати", emoji: "💸" },
  { id: "trends", label: "Тренди", emoji: "📈" },
  { id: "reviews", label: "Відгуки", emoji: "⭐" },
  { id: "resources", label: "Ресурси", emoji: "🪑" },
  { id: "shopping", label: "Закупки", emoji: "🛒" },
];

export function ReportsTabModern() {
  const [sub, setSub] = useState<SubTab>("money");
  const [range, setRange] = useState<PeriodValue>({ preset: "1m" });
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // Overlay-pattern щоб скрол + sub-tab + range зберігались при поверненні
  // з ClientDetail/MasterDetail. Дашборд лишається у DOM (display:none коли
  // відкрита деталь). useScrollMemory ловить scrollY у момент кліку і
  // відновлює його коли деталь закрилась.
  const detailOpen = !!selectedClientId || !!selectedMasterId;
  const captureScroll = useScrollMemory(detailOpen);
  const openClient = (id: string) => {
    captureScroll();
    setSelectedClientId(id);
  };
  const openMaster = (id: string) => {
    captureScroll();
    setSelectedMasterId(id);
  };
  return (
    <>
      <div style={{ display: detailOpen ? "none" : "block" }}>
        {/* 6 sub-tabs → красива bottom-sheet модалка замість native picker */}
        <SelectSheet<SubTab>
          title="📊 Аналітика"
          value={sub}
          onChange={(v) => setSub(v)}
          options={SUB_TABS.map((t) => ({
            value: t.id,
            label: `${t.emoji} ${t.label}`,
          }))}
        />

        {sub === "money" && (
          <AMoney
            range={range}
            setRange={setRange}
            onOpenMaster={openMaster}
            onOpenClient={openClient}
          />
        )}
        {sub === "expenses" && <AExpenses range={range} setRange={setRange} />}
        {sub === "trends" && (
          <ATrends
            range={range}
            setRange={setRange}
            onOpenMaster={openMaster}
          />
        )}
        {sub === "reviews" && (
          <AReviews
            range={range}
            setRange={setRange}
            onOpenClient={openClient}
          />
        )}
        {sub === "resources" && <AResources />}
        {sub === "shopping" && <AShopping />}
      </div>
      {selectedClientId && (
        <ClientDetailModern
          id={selectedClientId}
          onBack={() => setSelectedClientId(null)}
        />
      )}
      {selectedMasterId && (
        <MasterDetailModern
          id={selectedMasterId}
          onBack={() => setSelectedMasterId(null)}
        />
      )}
    </>
  );
}

// ---- 💰 Гроші ----

const SERVICE_LABEL_MOD: Record<string, string> = {
  manicure: "Манікюр",
  pedicure: "Педикюр",
  manicure_pedicure: "Манік + педик",
  other: "Інше",
};

const REFERRAL_LABEL_MOD: Record<string, string> = {
  instagram: "📷 Instagram",
  google_maps: "🗺️ Google Maps",
  tiktok: "🎵 TikTok",
  public: "📢 Паблік",
  targeted_ads: "🎯 Таргет",
  recommendation: "🤝 Порекомендували",
  business_card: "💳 Візитка",
  cross_marketing: "🔁 Крос-маркетинг",
};

function AMoney({
  range,
  setRange,
  onOpenMaster,
  onOpenClient,
}: {
  range: PeriodValue;
  setRange: (v: PeriodValue) => void;
  onOpenMaster?: (id: string) => void;
  onOpenClient?: (id: string) => void;
}) {
  const [source, setSource] = useState<"cash" | "bumpix">("cash");
  const [data, setData] = useState<any | null>(null);
  const [byMaster, setByMaster] = useState<any[]>([]);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<
    { items: { bucket: string; visits: number; bumpixRevenue: number; cashRevenue: number; cashTips: number }[] } | null
  >(null);
  // 4 розділи перенесені з легасі (v0.14.4): топ клієнтів, послуги, звідки
  // приходять клієнти, історія виплат — раніше в модерн взагалі не було.
  const [services, setServices] = useState<any[]>([]);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [topClients, setTopClients] = useState<any[]>([]);
  const [payouts, setPayouts] = useState<any[]>([]);
  // «Виручка по місяцях» — завжди від першого місяця з даними до сьогодні,
  // НЕЗАЛЕЖНО від вибраного періоду. Люба, 26.10.2026: «просто шоб було
  // прораховано за кожен місяць … з якого там бот, просто місяці виручки».
  // Тому окремий запит, а не похідне від timeline вище (той — по тижнях і
  // лише в межах обраного періоду).
  const [months, setMonths] = useState<
    { key: string; cash: number; bumpix: number; tips: number }[]
  >([]);

  const loadByMaster = () => {
    const { from, to } = periodToIsoRange(range);
    api.reports
      .byMaster(from, to)
      .then((d) => {
        const arr = (d as any[]).filter(
          (m) =>
            (m.visits ?? 0) > 0 ||
            (m.cashRevenue ?? 0) > 0 ||
            (m.bumpixRevenue ?? 0) > 0,
        );
        setByMaster(arr);
      })
      .catch(() => setByMaster([]));
  };

  useEffect(() => {
    const { from, to } = periodToIsoRange(range);
    api.reports.overview(from, to).then(setData).catch(() => setData(null));
    loadByMaster();
    api.reports
      .timeline({ from, to, bucket: "week" })
      .then((d) => setTimeline(d as any))
      .catch(() => setTimeline(null));
    api.reports
      .services(from, to)
      .then((d) => setServices(d as any[]))
      .catch(() => setServices([]));
    api.reports
      .topClients(from, to)
      .then((d) => setTopClients(d as any[]))
      .catch(() => setTopClients([]));
    api
      .listPayouts(from, to)
      .then((d) => setPayouts(d as any[]))
      .catch(() => setPayouts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => {
    const [y, m] = DATA_START_MONTH.split("-").map(Number);
    api.reports
      .timeline({
        from: new Date(y, m - 1, 1).toISOString(),
        to: new Date().toISOString(),
        bucket: "month",
      })
      .then((d: any) =>
        setMonths(
          (d?.items ?? [])
            .map((t: any) => ({
              key: String(t.bucket).slice(0, 7),
              cash: t.cashRevenue ?? 0,
              bumpix: t.bumpixRevenue ?? 0,
              tips: t.cashTips ?? 0,
            }))
            .filter((t: { key: string }) => t.key >= DATA_START_MONTH)
            .sort((a: { key: string }, b: { key: string }) =>
              a.key < b.key ? 1 : -1,
            ),
        ),
      )
      .catch(() => setMonths([]));
  }, []);

  // Referrals — не залежать від range, тягнемо разово.
  useEffect(() => {
    api.reports
      .referralSources()
      .then((d) => setReferrals(d as any[]))
      .catch(() => setReferrals([]));
  }, []);

  async function payMaster(m: any) {
    if (payingId) return;
    const name = m.name ?? "цьому майстру";
    const amount = m.pendingPayout ?? 0;
    if (!window.confirm(`Виплатити ${amount} грн майстерці ${name}?`)) return;
    setPayingId(m.masterId);
    try {
      const { from, to } = periodToIsoRange(range);
      await api.payoutMaster(m.masterId, from, to);
      loadByMaster();
    } catch (e) {
      alert(String(e));
    } finally {
      setPayingId(null);
    }
  }

  if (!data) return <div className="hint" style={{ marginTop: 18 }}>Завантаження…</div>;

  const revenue = source === "cash" ? data.cashRevenue ?? 0 : data.bumpixRevenue ?? 0;
  const tips = data.cashTips ?? 0;
  const avgCheck = source === "cash" ? data.cashAvgCheck ?? 0 : data.bumpixAvgCheck ?? 0;
  const studio = Math.round(revenue * 0.6);
  // Виплати майстрам — ТІЛЬКИ серверна цифра для каси. Після v0.30.4
  // cashRevenue означає «гроші, що зайшли» (без блогерок), і 40% від нього
  // занижували виплату: майстриня свої 40% отримує і за блогерку, бо їй
  // доплачує Люба. Сервер рахує mastersPayout від повної суми.
  const masterTakes =
    source === "cash" && typeof data.mastersPayout === "number"
      ? data.mastersPayout
      : Math.round(revenue * 0.4 + tips);

  return (
    <>
      <PeriodPicker value={range} onChange={setRange} />
      <Pills2
        a="💰 Telegram"
        b="📊 Bumpix"
        on={source === "cash"}
        onChange={(left) => setSource(left ? "cash" : "bumpix")}
      />
      <div className="nz-sec-sub" style={{ margin: "9px 4px 0" }}>
        Джерело: <b>{source === "cash" ? "Telegram" : "Bumpix"}</b> ·{" "}
        {source === "cash"
          ? "точні суми зі звітів майстрів"
          : "орієнтовно з календаря"}
      </div>

      {/* Top hero: revenue (focal) */}
      <div className="nz-salhero" style={{ padding: 17 }}>
        <div className="lbl">💰 Виручка</div>
        <div className="amt" style={{ fontSize: 36, color: "var(--green)" }}>
          {uah(revenue)} ₴
        </div>
        {tips > 0 && (
          <div className="nz-sec-sub" style={{ marginTop: 6 }}>
            + {uah(tips)} ₴ чайових
          </div>
        )}
        <div className="nz-payout-rows">
          <div className="nz-sumrow">
            <span className="k">Середній чек</span>
            <span className="vv">{uah(avgCheck)} ₴</span>
          </div>
          <div className="nz-sumrow">
            <span className="k">Дохід студії · 60%</span>
            <span className="vv" style={{ color: "var(--rose-600)" }}>
              {uah(studio)} ₴
            </span>
          </div>
          <div className="nz-sumrow">
            <span className="k">Виплати майстрам · 40% + чай</span>
            <span className="vv green-t">{uah(masterTakes)} ₴</span>
          </div>
        </div>
      </div>

      {/* Compact 3-column tile row for counts */}
      <div className="nz-kpis k3">
        <Kpi label="Візити" value={String(data.visits ?? 0)} />
        <Kpi label="Клієнти" value={String(data.uniqueClients ?? 0)} />
        <Kpi label="Нові" value={String(data.newClients ?? 0)} />
      </div>

      {months.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">🗓 Виручка по місяцях</div>
          <div className="nz-sec-sub" style={{ marginBottom: 6 }}>
            Тицьни місяць — і весь екран покаже саме його.
          </div>
          {months.map((mo) => {
            const isCurrent = mo.key === currentMonthKey();
            const selected = isCurrent
              ? range.preset === "1m"
              : range.preset === "month" && range.month === mo.key;
            const v = source === "cash" ? mo.cash : mo.bumpix;
            return (
              <button
                key={mo.key}
                className="nz-sumrow nz-monthrow"
                data-on={selected}
                onClick={() =>
                  setRange(
                    isCurrent
                      ? { preset: "1m" }
                      : { preset: "month", month: mo.key },
                  )
                }
              >
                <span className="k">
                  {monthTitle(mo.key)}
                </span>
                <span className="vv">
                  {uah(v)} ₴
                  {source === "cash" && mo.tips > 0 && (
                    <span className="tips"> + {uah(mo.tips)} чай</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {timeline && timeline.items.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">📈 Виручка по тижнях</div>
          <VBars
            data={timeline.items.map((t) => ({
              v: source === "cash" ? t.cashRevenue : t.bumpixRevenue,
              n: uah(source === "cash" ? t.cashRevenue : t.bumpixRevenue),
              x: shortBucket(t.bucket),
            }))}
          />
        </div>
      )}

      {byMaster.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">💁‍♀️ По майстрах</div>
          {byMaster.slice(0, 8).map((m: any) => (
            <div className="nz-mcard" key={m.masterId}>
              <button
                className="mh"
                onClick={() => onOpenMaster?.(m.masterId)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  padding: 0,
                }}
              >
                <span className="mn">
                  {m.name} <ChevronRight />
                </span>
                <span className="mv">{m.visits} віз.</span>
              </button>
              <div className="mgrid">
                <Mg label="Виручка" v={uah(m.cashRevenue ?? m.bumpixRevenue ?? 0)} />
                <Mg label="Майстру" v={uah(m.masterTakes ?? Math.round((m.cashRevenue ?? 0) * 0.4))} cls="green-t" />
                <Mg label="Виплачено" v={uah(m.paidOut ?? 0)} />
                <Mg
                  label="Студії"
                  v={uah(Math.round((m.cashRevenue ?? 0) * 0.6))}
                  style={{ color: "var(--rose-600)" }}
                />
              </div>
              {(m.pendingPayout ?? 0) > 0 && (
                <button
                  type="button"
                  className="nz-paybtn"
                  disabled={payingId === m.masterId}
                  onClick={() => payMaster(m)}
                >
                  {payingId === m.masterId
                    ? "Зачекай…"
                    : `💸 Виплатити ${uah(m.pendingPayout)}`}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 🏆 Топ клієнтів по грошах */}
      {topClients.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">🏆 Топ клієнтів по грошах</div>
          <div className="nz-sec-sub">
            Хто найбільше ходить і платить. Тицяй — відкриється профіль.
          </div>
          {topClients.map((c: any, i: number) => {
            const name = `${c.firstName} ${c.lastName ?? ""}`.trim();
            const avg = c.visits > 0 ? c.spent / c.visits : 0;
            const rank =
              i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
            return (
              <button
                key={c.clientId}
                type="button"
                onClick={() => onOpenClient?.(c.clientId)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: 12,
                  padding: "10px 14px",
                  marginTop: 8,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      minWidth: 28,
                      fontWeight: 700,
                      color: "var(--ink-3)",
                    }}
                  >
                    {rank}
                  </span>
                  <span style={{ fontWeight: 700, flex: 1, color: "var(--ink)" }}>
                    {name}
                  </span>
                  <span style={{ fontWeight: 800, color: "var(--green)" }}>
                    {uah(c.spent)} ₴
                  </span>
                </div>
                <div
                  className="hint"
                  style={{ fontSize: 12, marginTop: 3, marginLeft: 36 }}
                >
                  {c.visits} віз. · сер. {uah(avg)} ₴
                  {c.tips > 0 ? ` · 💸 ${uah(c.tips)} чай` : ""}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* 💅 Послуги */}
      {services.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">💅 Послуги</div>
          <div className="nz-sec-sub">Кількість візитів з Bumpix</div>
          {(() => {
            const maxV = Math.max(...services.map((s: any) => s.visits), 1);
            return services.map((s: any) => (
              <div key={s.service} className="nz-hbar" style={{ marginTop: 10 }}>
                <div className="hh">
                  <span className="lab">
                    {SERVICE_LABEL_MOD[s.service] ?? s.service}
                  </span>
                  <span className="val">
                    {s.visits} ({uah(s.revenue)} ₴)
                  </span>
                </div>
                <div className="track">
                  <div
                    className="fill"
                    data-hl="true"
                    style={{ width: Math.max(4, (s.visits / maxV) * 100) + "%" }}
                  />
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* 📍 Звідки приходять клієнти */}
      {referrals.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">📍 Звідки приходять клієнти</div>
          <div className="nz-sec-sub">
            З відповідей на «звідки про нас дізналися» (за весь час)
          </div>
          {(() => {
            const maxV = Math.max(...referrals.map((r: any) => r.count), 1);
            return referrals.map((r: any) => (
              <div key={r.source} className="nz-hbar" style={{ marginTop: 10 }}>
                <div className="hh">
                  <span className="lab">
                    {REFERRAL_LABEL_MOD[r.source] ?? r.source}
                  </span>
                  <span className="val">{r.count}</span>
                </div>
                <div className="track">
                  <div
                    className="fill"
                    data-hl="true"
                    style={{ width: Math.max(4, (r.count / maxV) * 100) + "%" }}
                  />
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* 📒 Виплати — історія */}
      {payouts.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">📒 Виплати</div>
          <div className="nz-sec-sub">
            Всього виплачено:{" "}
            <b>
              {uah(payouts.reduce((s, p: any) => s + (p.amount ?? 0), 0))} ₴
            </b>
          </div>
          {payouts.map((p: any) => {
            const d = new Date(p.paidAt);
            const dateStr = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
            return (
              <div
                key={p.id}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: 12,
                  padding: "10px 14px",
                  marginTop: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      minWidth: 50,
                      fontWeight: 700,
                      color: "var(--ink-3)",
                    }}
                  >
                    {dateStr}
                  </span>
                  <span style={{ fontWeight: 700, flex: 1, color: "var(--ink)" }}>
                    {p.masterName}
                  </span>
                  <span style={{ fontWeight: 800, color: "var(--green)" }}>
                    {uah(p.amount)} ₴
                  </span>
                </div>
                <div className="hint" style={{ fontSize: 11, marginTop: 4 }}>
                  40%: {uah(p.fromPrices ?? 0)} ₴ · чай: {uah(p.fromTips ?? 0)} ₴
                  · {p.cashEntryCount ?? 0} записів
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---- 💸 Витрати ----

function AExpenses({
  range,
  setRange,
}: {
  range: PeriodValue;
  setRange: (v: PeriodValue) => void;
}) {
  const [profit, setProfit] = useState<any | null>(null);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const { from, to } = periodToIsoRange(range);
    api.reports.profit(from, to).then(setProfit).catch(() => setProfit(null));
    api
      .expenses(from, to)
      .then((d) => setExpenses(d as any[]))
      .catch(() => setExpenses([]));
  }, [range, reloadKey]);

  return (
    <>
      <PeriodPicker value={range} onChange={setRange} />
      <div className="nz-kpis">
        <Kpi label="💰 Виручка студії" value={`${uah(profit?.studioGross ?? 0)} ₴`} cls="green" />
        <Kpi label="📉 Витрати" value={`${uah(profit?.expenses ?? 0)} ₴`} cls="red" />
        <div className="nz-kpi span">
          <div className="l">📈 Чистий прибуток</div>
          <div className={"v " + ((profit?.studioNet ?? 0) >= 0 ? "green" : "red")}>
            {uah(profit?.studioNet ?? 0)} ₴
          </div>
        </div>
      </div>

      <ExpenseForm onAdded={() => setReloadKey((k) => k + 1)} />

      <div className="nz-sec-title" style={{ margin: "20px 4px 10px" }}>
        Останні витрати
      </div>
      {expenses.length === 0 ? (
        <div className="nz-sec-sub" style={{ textAlign: "center", padding: 24 }}>
          Витрат у цьому періоді нема
        </div>
      ) : (
        expenses.slice(0, 30).map((e: any) => {
          const cat = e.categoryName ?? e.category ?? "Інше";
          return (
            <div className="nz-expense" key={e.id}>
              <div className="d">{shortDate(e.occurredOn)}</div>
              <div className="cat">
                {categoryEmoji(cat)} {cat}
                {e.note ? ` · ${e.note}` : ""}
              </div>
              <div className="amt">−{uah(e.amount)} ₴</div>
            </div>
          );
        })
      )}
    </>
  );
}

// ---- ➕ Додати витрату (форма) ----

function ExpenseForm({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState<
    { id: string; name: string; kind: "fixed" | "variable" }[]
  >([]);
  const [categoryId, setCategoryId] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [dateOpen, setDateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .expenseCategories()
      .then((d) =>
        setCats(
          (d as any[])
            .filter((c) => !c.archived)
            .map((c) => ({ id: c.id, name: c.name, kind: c.kind })),
        ),
      )
      .catch(() => setCats([]));
  }, [open]);

  async function submit() {
    if (!categoryId || !amount.trim() || busy) return;
    setBusy(true);
    try {
      await api.addExpense({
        occurredOn: date,
        amount: Number(amount.replace(",", ".")) || 0,
        categoryId,
        note: note.trim() || undefined,
      });
      setAmount("");
      setNote("");
      setCategoryId("");
      setOpen(false);
      onAdded();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        className="nz-create"
        style={{ marginTop: 14 }}
        onClick={() => setOpen(true)}
      >
        ➕ Додати витрату
      </button>
    );
  }

  const catOptions = cats.map((c) => ({
    value: c.id,
    label: `${CATEGORY_EMOJI[c.name] ?? "💸"} ${c.name}`,
    group: c.kind === "fixed" ? "🗓 Постійні" : "🌀 Змінні",
  }));
  const selectedCat = cats.find((c) => c.id === categoryId);
  const dateLabel = date
    ? `${date.split("-")[2]}.${date.split("-")[1]}.${date.split("-")[0]}`
    : "Сьогодні";

  return (
    <div
      className="nz-sec"
      style={{
        marginTop: 14,
        padding: 14,
        background: "var(--surface)",
        border: "1px solid var(--rose-200)",
        borderRadius: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div className="nz-sec-title" style={{ margin: 0 }}>
          ➕ Додати витрату
        </div>
        <button
          onClick={() => setOpen(false)}
          aria-label="Закрити"
          title="Закрити"
          style={{
            background: "transparent",
            border: 0,
            fontSize: 18,
            color: "var(--ink-3)",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          ✕
        </button>
      </div>

      <div className="nz-fieldrow">
        <label>Категорія</label>
        <SelectSheet
          title="💸 Категорія витрати"
          value={categoryId}
          onChange={setCategoryId}
          placeholder={selectedCat ? selectedCat.name : "— Виберіть категорію —"}
          options={catOptions}
        />
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
      >
        <div className="nz-fieldrow" style={{ marginBottom: 0 }}>
          <label>Сума, ₴</label>
          <input
            className="nz-input"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
            inputMode="decimal"
            placeholder="0"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid var(--line)",
              borderRadius: 12,
              background: "var(--surface)",
              color: "var(--ink)",
              fontSize: 14,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>
        <div className="nz-fieldrow" style={{ marginBottom: 0 }}>
          <label>Дата</label>
          <button
            type="button"
            className="nz-sheet-trigger"
            onClick={() => setDateOpen(true)}
            style={{ marginBottom: 0 }}
          >
            <span className="nz-dropdown-sel">📅 {dateLabel}</span>
            <span className="nz-dropdown-caret">▾</span>
          </button>
        </div>
      </div>

      <div className="nz-fieldrow" style={{ marginTop: 10 }}>
        <label>Коментар (необовʼязково)</label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="напр. оплата за червень"
          style={{
            width: "100%",
            padding: "10px 12px",
            border: "1px solid var(--line)",
            borderRadius: 12,
            background: "var(--surface)",
            color: "var(--ink)",
            fontSize: 14,
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
      </div>

      <button
        className="nz-btn primary"
        disabled={busy || !categoryId || !amount.trim()}
        onClick={submit}
        style={{ width: "100%", marginTop: 14, height: 46 }}
      >
        {busy ? "Зберігаємо…" : "Зберегти витрату"}
      </button>

      <DateSheet
        mode="single"
        open={dateOpen}
        onClose={() => setDateOpen(false)}
        title="📅 Дата витрати"
        onPickDate={(iso) => setDate(iso)}
        minDate={new Date(2020, 0, 1)}
      />
    </div>
  );
}

const CATEGORY_EMOJI: Record<string, string> = {
  Оренда: "🏠",
  Комунальні: "💡",
  Матеріали: "💅",
  "Сервіс (вода/кава/солодке)": "☕",
  Обладнання: "🛠️",
  "Інтернет + телефон": "📱",
  "ЗП адміну": "👤",
  CRM: "💻",
  Корпоративи: "🥂",
  Уніформа: "👕",
  "Подарунки (клієнтам / майстрам)": "🎁",
  Реклама: "📢",
  Поліграфія: "🖨️",
  Блогери: "📣",
  Ремонт: "🔧",
  Доставка: "📦",
  "Компенсація клієнтам": "💸",
};
function categoryEmoji(c: string): string {
  return CATEGORY_EMOJI[c] ?? "💸";
}

// ---- 📈 Тренди ----

function ATrends({
  range,
  setRange,
  onOpenMaster,
}: {
  range: PeriodValue;
  setRange: (v: PeriodValue) => void;
  onOpenMaster?: (id: string) => void;
}) {
  const [forecast, setForecast] = useState<any | null>(null);
  const [retention, setRetention] = useState<any | null>(null);
  const [ratings, setRatings] = useState<any[]>([]);
  const [tips, setTips] = useState<any[]>([]);
  const [heatmap, setHeatmap] = useState<{ day: number; hour: number; visits: number }[]>([]);

  useEffect(() => {
    const { from, to } = periodToIsoRange(range);
    api.reports.forecast().then(setForecast).catch(() => setForecast(null));
    api.reports.retention(from, to).then(setRetention).catch(() => setRetention(null));
    api.reports
      .masterRatings(from, to)
      .then((d) => setRatings(d as any[]))
      .catch(() => setRatings([]));
    api.reports
      .byMaster(from, to)
      .then((d) => {
        const arr = d as any[];
        arr.sort((a, b) => (b.cashTips ?? 0) - (a.cashTips ?? 0));
        setTips(arr.filter((m) => (m.cashTips ?? 0) > 0));
      })
      .catch(() => setTips([]));
    api.reports
      .heatmap(from, to)
      .then((d) =>
        setHeatmap(
          // API повертає ISODOW: 1=Пн..7=Нд. Грід індексується 0..6 (масив
          // ["Пн".."Нд"]), тому віднімаємо 1, інакше Пн порожній, а Нд
          // показує суботу (зсув на день — помічено замовницею).
          (d as { dow: number; hour: number; visits: number }[]).map((h) => ({
            day: h.dow - 1,
            hour: h.hour,
            visits: h.visits,
          })),
        ),
      )
      .catch(() => setHeatmap([]));
  }, [range]);

  return (
    <>
      <PeriodPicker value={range} onChange={setRange} />

      {forecast && (
        <div className="nz-sec">
          <div className="nz-sec-title">🔮 Прогноз на цей місяць</div>
          <div
            style={{
              fontSize: 34,
              fontWeight: 800,
              color: "var(--green)",
              marginTop: 8,
              letterSpacing: "-1px",
              whiteSpace: "nowrap",
            }}
          >
            {uah(forecast.forecast?.cash ?? 0)} ₴
            {forecast.prevMonth?.cash != null && forecast.prevMonth.cash > 0 && (
              <span style={{ fontSize: 18, marginLeft: 8 }}>
                {(() => {
                  const delta = Math.round(
                    (((forecast.forecast?.cash ?? 0) -
                      forecast.prevMonth.cash) /
                      forecast.prevMonth.cash) *
                      100,
                  );
                  return `${delta >= 0 ? "↑" : "↓"} ${Math.abs(delta)}%`;
                })()}
              </span>
            )}
          </div>
          <div className="nz-sec-sub">
            Минулого місяця: {uah(forecast.prevMonth?.cash ?? 0)} ₴ · набрали{" "}
            {uah(forecast.mtd?.cash ?? 0)} за {forecast.daysPassed ?? 0} днів з{" "}
            {forecast.daysInMonth ?? 30}
          </div>
        </div>
      )}

      {retention && (
        <div className="nz-sec">
          <div className="nz-sec-title">📊 Retention · період</div>
          <div
            style={{
              fontSize: 34,
              fontWeight: 800,
              color:
                retention.rate >= 60
                  ? "var(--green)"
                  : retention.rate >= 30
                    ? "var(--amber-ink)"
                    : "var(--red-ink)",
              marginTop: 6,
            }}
          >
            {retention.rate}%
          </div>
          <div className="nz-sec-sub">
            {retention.returned} з {retention.newClients} новеньких клієнток
            повернулись на 2-й візит
          </div>
        </div>
      )}

      {ratings.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">⭐ Рейтинг майстрів</div>
          {ratings.map((r: any) => {
            const total = r.positive + r.negative;
            const pct = total > 0 ? Math.round((r.positive / total) * 100) : 0;
            const cls =
              pct >= 80 ? "green-t" : pct >= 50 ? "amber-t" : "red-t";
            return (
              <button
                className="nz-rate"
                key={r.masterId}
                onClick={() => onOpenMaster?.(r.masterId)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <span className="nm">{r.name}</span>
                <span className="th">
                  👍 {r.positive} · 👎 {r.negative}
                </span>
                <span className={"pc " + cls}>{pct}%</span>
              </button>
            );
          })}
        </div>
      )}

      {tips.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">💸 Лідерборд чайових</div>
          {tips.slice(0, 5).map((t: any, i: number) => (
            <button
              className="nz-medal"
              key={t.masterId}
              onClick={() => onOpenMaster?.(t.masterId)}
              style={{
                width: "100%",
                background: "transparent",
                border: 0,
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
              }}
            >
              <span className="m">
                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`}
              </span>
              <span className="nm">{t.name}</span>
              <span className="vv">{uah(t.cashTips)} ₴</span>
            </button>
          ))}
        </div>
      )}

      {heatmap.length > 0 && (
        <HeatmapBlock items={heatmap} />
      )}
    </>
  );
}

function HeatmapBlock({ items }: { items: { day: number; hour: number; visits: number }[] }) {
  // Build a grid: days (0=Mon..6=Sun), hours typically 9..21
  const days = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
  const hours: number[] = [];
  for (const it of items) {
    if (!hours.includes(it.hour)) hours.push(it.hour);
  }
  hours.sort((a, b) => a - b);
  const max = Math.max(...items.map((i) => i.visits), 1);
  const lookup = new Map<string, number>();
  for (const it of items) lookup.set(`${it.day}:${it.hour}`, it.visits);
  const colour = (v: number) =>
    v === 0 ? "var(--chip)" : `hsl(340 64% ${80 - Math.round((v / max) * 30)}%)`;
  return (
    <div className="nz-sec">
      <div className="nz-sec-title">🔥 Завантаженість</div>
      <div className="nz-heat">
        <div className="hrow">
          <span />
          {days.map((d) => (
            <span className="hd" key={d}>
              {d}
            </span>
          ))}
        </div>
        {hours.map((h) => (
          <div className="hrow" key={h}>
            <span className="hl">{h}:00</span>
            {days.map((dn, di) => {
              const v = lookup.get(`${di}:${h}`) ?? 0;
              return (
                <span
                  className="hc"
                  key={di}
                  title={`${dn} ${h}:00 — ${v || 0} візит${v === 1 ? "" : "ів"}`}
                  style={{ background: colour(v), color: v ? "#fff" : "transparent" }}
                >
                  {v || ""}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- ⭐ Відгуки ----

function AReviews({
  range,
  setRange,
  onOpenClient,
}: {
  range: PeriodValue;
  setRange: (v: PeriodValue) => void;
  onOpenClient?: (id: string) => void;
}) {
  const [data, setData] = useState<any | null>(null);
  const [filter, setFilter] = useState<"all" | "positive" | "mixed" | "negative">("all");

  useEffect(() => {
    const { from, to } = periodToIsoRange(range);
    api.reviews(from, to).then(setData).catch(() => setData(null));
  }, [range]);

  if (!data) return <div className="hint" style={{ marginTop: 18 }}>Завантаження…</div>;

  const filtered =
    filter === "all" ? data.items : data.items.filter((i: any) => i.sentiment === filter);

  return (
    <>
      <PeriodPicker value={range} onChange={setRange} />
      <div className="nz-kpis k3">
        <Kpi label="👍 Позитив" value={String(data.counts.positive)} cls="green" />
        <div className="nz-kpi">
          <div className="l">😐 50/50</div>
          <div className="v" style={{ color: "var(--amber-ink)" }}>
            {data.counts.mixed}
          </div>
        </div>
        <Kpi label="👎 Скарги" value={String(data.counts.negative)} cls="red" />
      </div>

      {data.googleFunnel && (
        <div className="nz-sec">
          <div className="nz-sec-title">🌟 Google-воронка</div>
          <div className="nz-sumrow">
            <span className="k">Попросили лишити (👍)</span>
            <span className="vv">{data.googleFunnel.asked}</span>
          </div>
          <div className="nz-sumrow">
            <span className="k">Реально лишили</span>
            <span className="vv green-t">{data.googleFunnel.left}</span>
          </div>
          <div className="nz-sumrow total">
            <span className="k">Конверсія</span>
            <span className="vv red-t">
              {data.googleFunnel.asked > 0
                ? Math.round(
                    (data.googleFunnel.left / data.googleFunnel.asked) * 100,
                  )
                : 0}
              %
            </span>
          </div>
        </div>
      )}

      <div className="nz-segwrap" style={{ marginTop: 16 }}>
        <button className="nz-seg" data-on={filter === "all"} onClick={() => setFilter("all")}>
          Усі
        </button>
        <button className="nz-seg" data-on={filter === "positive"} onClick={() => setFilter("positive")}>
          👍
        </button>
        <button className="nz-seg" data-on={filter === "mixed"} onClick={() => setFilter("mixed")}>
          😐
        </button>
        <button className="nz-seg" data-on={filter === "negative"} onClick={() => setFilter("negative")}>
          👎
        </button>
      </div>

      {filtered.slice(0, 20).map((r: any) => (
        <button
          className="nz-review"
          key={r.id}
          onClick={() => r.clientId && onOpenClient?.(r.clientId)}
          style={{
            width: "100%",
            border: "1px solid var(--line)",
            cursor: r.clientId ? "pointer" : "default",
            fontFamily: "inherit",
            textAlign: "left",
            display: "block",
          }}
        >
          <div className="rt">
            <span
              className={
                "sent " +
                (r.sentiment === "positive"
                  ? "green-t"
                  : r.sentiment === "negative"
                    ? "red-t"
                    : "amber-t")
              }
            >
              {r.sentiment === "positive" ? "👍 Позитив" : r.sentiment === "negative" ? "👎 Скарга" : "😐 50/50"}
            </span>
            <span className="date">{shortDate(r.createdAt)}</span>
          </div>
          <div className="who">{r.clientName}</div>
          {r.masterName && <div className="by">💁‍♀️ {r.masterName}</div>}
          {r.text && <div className="quote">«{r.text}»</div>}
        </button>
      ))}
    </>
  );
}

// ---- 🪑 Ресурси ----

function AResources() {
  const [limits, setLimits] = useState<any[]>([]);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [conflictTab, setConflictTab] = useState<"future" | "past">("future");
  const [addOpen, setAddOpen] = useState(false);
  const [addLabel, setAddLabel] = useState("");
  const [addPattern, setAddPattern] = useState("");
  const [addCapacity, setAddCapacity] = useState("2");
  const [addBusy, setAddBusy] = useState(false);

  const loadLimits = () =>
    api.resourceLimits().then((d) => setLimits(d as any[])).catch(() => setLimits([]));

  useEffect(() => {
    loadLimits();
    api
      .conflicts(conflictTab)
      .then((d) => setConflicts(d as any[]))
      .catch(() => setConflicts([]));
  }, [conflictTab]);

  async function toggleLimit(l: any) {
    try {
      await api.patchResourceLimit(l.id, { isActive: !l.isActive });
      loadLimits();
    } catch (e) {
      alert(String(e));
    }
  }

  async function removeLimit(l: any) {
    if (!window.confirm(`Видалити ліміт «${l.label}»?`)) return;
    try {
      await api.deleteResourceLimit(l.id);
      loadLimits();
    } catch (e) {
      alert(String(e));
    }
  }

  async function addLimit() {
    if (!addLabel.trim() || !addPattern.trim()) return;
    setAddBusy(true);
    try {
      await api.addResourceLimit({
        label: addLabel.trim(),
        pattern: addPattern.trim(),
        capacity: Number(addCapacity) || 1,
      });
      setAddLabel("");
      setAddPattern("");
      setAddCapacity("2");
      setAddOpen(false);
      loadLimits();
    } catch (e) {
      alert(String(e));
    } finally {
      setAddBusy(false);
    }
  }

  return (
    <>
      <div className="nz-sec">
        <div className="nz-sec-title">⚠️ Конфлікти розкладу</div>
        <div className="nz-segwrap" style={{ marginTop: 11 }}>
          <button
            className="nz-seg"
            data-on={conflictTab === "future"}
            onClick={() => setConflictTab("future")}
          >
            🔮 Майбутні
            {conflictTab === "future" && (
              <span className="nz-subcount">{conflicts.length}</span>
            )}
          </button>
          <button
            className="nz-seg"
            data-on={conflictTab === "past"}
            onClick={() => setConflictTab("past")}
          >
            📜 Минулі
            {conflictTab === "past" && (
              <span className="nz-subcount">{conflicts.length}</span>
            )}
          </button>
        </div>
        {conflicts.length === 0 && (
          <div className="nz-sec-sub" style={{ marginTop: 10 }}>
            Все чисто. Жодних накладок попереду 🎉
          </div>
        )}
      </div>

      <div className="nz-sec">
        <div className="nz-sec-title">🪑 Ліміти ресурсів</div>
        <div className="nz-sec-sub">
          Назва — для тебе; шаблон — слово в назві послуги. Якщо одночасно
          запланованих візитів &gt; ліміту — приходить пуш.
        </div>
        {limits.length === 0 ? (
          <div className="nz-sec-sub" style={{ marginTop: 10 }}>
            Ще нема жодного ліміту. Додай, напр. «Педикюр → крісла → 2».
          </div>
        ) : (
          limits.map((l: any) => (
            <div
              className="nz-limit"
              key={l.id}
              style={{
                marginTop: 12,
                opacity: l.isActive ? 1 : 0.55,
              }}
            >
              <div className="main">
                <div className="nm">{l.label}</div>
                <div className="sb">
                  «{l.pattern}» · {l.capacity} місць{l.isActive ? "" : " · вимкнено"}
                </div>
              </div>
              <button
                type="button"
                className={"nz-toggle" + (l.isActive ? "" : " off")}
                onClick={() => toggleLimit(l)}
                title={l.isActive ? "Вимкнути ліміт" : "Увімкнути ліміт"}
              />
              <button
                type="button"
                onClick={() => removeLimit(l)}
                title="Видалити ліміт"
                style={{
                  marginLeft: 8,
                  background: "transparent",
                  border: 0,
                  color: "#C13030",
                  fontSize: 16,
                  cursor: "pointer",
                  padding: "0 4px",
                }}
              >
                🗑
              </button>
            </div>
          ))
        )}

        {addOpen ? (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: "var(--surface-2)",
              borderRadius: 12,
              border: "1px solid var(--rose-200)",
            }}
          >
            <div className="nz-fieldrow" style={{ marginBottom: 8 }}>
              <label>Назва</label>
              <input
                className="nz-input"
                value={addLabel}
                onChange={(e) => setAddLabel(e.target.value)}
                placeholder="напр. Крісла педикюру"
              />
            </div>
            <div className="nz-fieldrow" style={{ marginBottom: 8 }}>
              <label>Шаблон (слово в послузі)</label>
              <input
                className="nz-input"
                value={addPattern}
                onChange={(e) => setAddPattern(e.target.value)}
                placeholder="напр. педикюр"
              />
            </div>
            <div className="nz-fieldrow" style={{ marginBottom: 12 }}>
              <label>Місць</label>
              <input
                className="nz-input"
                value={addCapacity}
                onChange={(e) =>
                  setAddCapacity(e.target.value.replace(/[^0-9]/g, ""))
                }
                inputMode="numeric"
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="nz-paybtn"
                disabled={addBusy || !addLabel.trim() || !addPattern.trim()}
                onClick={addLimit}
                style={{ flex: 1 }}
              >
                {addBusy ? "Збереження…" : "✓ Зберегти"}
              </button>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                style={{
                  flex: 1,
                  background: "var(--rose-50)",
                  border: 0,
                  borderRadius: 12,
                  color: "var(--rose-700)",
                  fontFamily: "inherit",
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: "pointer",
                  height: 44,
                }}
              >
                Скасувати
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="nz-create"
            style={{ marginTop: 12 }}
            onClick={() => setAddOpen(true)}
          >
            <span className="nz-ic">＋</span> Додати ліміт
          </button>
        )}
      </div>
    </>
  );
}

// ---- 🛒 Закупки ----

function AShopping() {
  const [items, setItems] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<any[] | null>(null);

  function load() {
    api.shoppingList("pending").then((d) => setItems(d as any[])).catch(() => setItems([]));
  }
  useEffect(() => {
    load();
  }, []);

  async function done(id: string) {
    await api.shoppingDone(id);
    load();
    if (history) {
      api.shoppingList("done,cancelled").then((d) => setHistory(d as any[]));
    }
  }
  async function cancel(id: string) {
    if (!window.confirm("Відмінити цей запит?")) return;
    await api.shoppingCancel(id);
    load();
  }

  return (
    <>
      <div className="nz-sec-sub" style={{ margin: "22px 4px 6px", lineHeight: 1.5 }}>
        Майстри пишуть боту <b>/buy текст</b> або «купити: гель-лак». Тут —
        їхній список запитів.
      </div>
      {items.length === 0 ? (
        <div className="nz-sec" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 38 }}>🎉</div>
          <div className="nz-sec-title" style={{ justifyContent: "center", marginTop: 6 }}>
            Закупок нема
          </div>
        </div>
      ) : (
        items.map((it: any) => (
          <div className="nz-buy" key={it.id}>
            <div className="bh">
              👤 {it.master.name} · {shortDate(it.createdAt)}
            </div>
            <div className="bt">{it.text}</div>
            <div className="ba">
              <button className="nz-btn green flex" onClick={() => done(it.id)}>
                ✓ Куплено
              </button>
              <button className="nz-btn flex danger-ghost" onClick={() => cancel(it.id)}>
                ✕ Відмінити
              </button>
            </div>
          </div>
        ))
      )}
      <button
        className="nz-showmore"
        style={{ marginTop: 12 }}
        onClick={() => {
          const next = !showHistory;
          setShowHistory(next);
          if (next && !history) {
            api.shoppingList("done,cancelled").then((d) => setHistory(d as any[]));
          }
        }}
      >
        {showHistory ? "▴ Сховати історію" : "▾ Показати історію"}
      </button>
      {showHistory && history && (
        <div style={{ marginTop: 10 }}>
          {history.slice(0, 20).map((it: any) => (
            <div className="nz-buy" key={it.id} style={{ opacity: 0.7 }}>
              <div className="bh">
                👤 {it.master.name} ·{" "}
                {it.status === "done" ? "✅ Куплено" : "✕ Відмінено"}
              </div>
              <div className="bt">{it.text}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---- Shared bits ----

function Pills2({
  a,
  b,
  on,
  onChange,
}: {
  a: string;
  b: string;
  on: boolean;
  onChange: (left: boolean) => void;
}) {
  return (
    <div className="nz-segwrap" style={{ marginTop: 13 }}>
      <button className="nz-seg" data-on={on} onClick={() => onChange(true)}>
        {a}
      </button>
      <button className="nz-seg" data-on={!on} onClick={() => onChange(false)}>
        {b}
      </button>
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  cls,
}: {
  label: string;
  value: string;
  sub?: string;
  cls?: "green" | "red" | "plum" | "blue" | "amber";
}) {
  return (
    <div className="nz-kpi">
      <div className="l">{label}</div>
      <div className={"v " + (cls ?? "")}>{value}</div>
      {sub && <div className="s">{sub}</div>}
    </div>
  );
}

function Mg({
  label,
  v,
  cls,
  style,
}: {
  label: string;
  v: string;
  cls?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className="g">
      <div className="gl">{label}</div>
      <div className={"gv" + (cls ? " " + cls : "")} style={style}>
        {v}
      </div>
    </div>
  );
}

function VBars({
  data,
}: {
  data: { v: number; n: string; x: string }[];
}) {
  const max = Math.max(...data.map((d) => d.v), 1);
  let peakIdx = 0;
  data.forEach((d, i) => {
    if (d.v >= data[peakIdx]!.v) peakIdx = i;
  });
  return (
    <div className="nz-bars">
      {data.map((d, i) => (
        <div className="nz-bar" key={i} title={`${d.x}: ${d.n}`}>
          <div className="n" data-peak={i === peakIdx}>
            {d.n}
          </div>
          <div className="lane">
            <div
              className="col"
              data-peak={i === peakIdx}
              style={{ height: Math.max(3, (d.v / max) * 100) + "%" }}
            />
          </div>
          <div className="x">{d.x}</div>
        </div>
      ))}
    </div>
  );
}

function ChevronRight() {
  return (
    <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
      <path
        d="m1.5 1 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Точна сума до 1 грн з розділювачем тисяч ("16 057", не "16k") — люди
// плутались у заокруглених «k». Групування пробілами, знак зберігається.
function uah(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function shortBucket(iso: string): string {
  // Тільки початок тижня — щоб вмістилось у 36px бара без перенесень.
  const d = new Date(iso);
  return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}
